import { useForm } from '@inertiajs/react'
import { NotaryPortalLayout } from '@/components/layout/NotaryPortalLayout'
import { FormSection } from '@/components/backoffice/FormSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { NotaryDetailed } from '@/types'

type Props = {
  notary: NotaryDetailed
}

const SESSION_MODE_OPTIONS = [
  { value: 'in_person', label: 'In Person' },
  { value: 'ron', label: 'Remote Online (RON)' },
  { value: 'rin', label: 'Remote Ink (RIN)' },
]

export default function ProfileEdit({ notary }: Props) {
  const { data, setData, patch, processing, errors } = useForm({
    phone: notary.phone || '',
    bio: notary.bio || '',
    session_modes: notary.session_modes || [],
    commission_number: notary.commission_number,
    commission_state: notary.commission_state,
    commission_expiry: notary.commission_expiry,
  })

  const handleSessionModeToggle = (mode: string, checked: boolean) => {
    if (checked) {
      setData('session_modes', [...data.session_modes, mode])
    } else {
      setData('session_modes', data.session_modes.filter((m) => m !== mode))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch('/notary_portal/profile', {
      data: { notary: data },
    })
  }

  return (
    <NotaryPortalLayout
      title="Edit Profile"
      breadcrumbs={[
        { label: 'Notary Portal', href: '/notary_portal/dashboard' },
        { label: 'Profile', href: '/notary_portal/profile' },
        { label: 'Edit' },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Edit Profile</h1>
          <p className="text-sm text-muted-foreground">Update your notary profile information</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <FormSection title="Contact">
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Phone</label>
              <Input
                className="h-8 text-sm"
                type="tel"
                value={data.phone}
                onChange={(e) => setData('phone', e.target.value)}
                placeholder="+1 (555) 000-0000"
              />
              {errors.phone && <p className="text-[12px] text-destructive">{errors.phone}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Bio</label>
              <Textarea
                className="text-sm min-h-20"
                value={data.bio}
                onChange={(e) => setData('bio', e.target.value)}
                placeholder="A brief description of your notary experience..."
              />
              {errors.bio && <p className="text-[12px] text-destructive">{errors.bio}</p>}
            </div>
          </FormSection>

          <FormSection title="Session Modes">
            <div className="space-y-3">
              {SESSION_MODE_OPTIONS.map((option) => (
                <label key={option.value} className="flex items-center gap-2.5 cursor-pointer">
                  <Checkbox
                    checked={data.session_modes.includes(option.value)}
                    onCheckedChange={(checked) => handleSessionModeToggle(option.value, checked === true)}
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
              {errors.session_modes && <p className="text-[12px] text-destructive">{errors.session_modes}</p>}
            </div>
          </FormSection>

          <FormSection title="Commission Details">
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Commission Number</label>
              <Input
                className="h-8 text-sm"
                value={data.commission_number}
                onChange={(e) => setData('commission_number', e.target.value)}
              />
              {errors.commission_number && <p className="text-[12px] text-destructive">{errors.commission_number}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Commission State</label>
              <Input
                className="h-8 text-sm"
                value={data.commission_state}
                onChange={(e) => setData('commission_state', e.target.value)}
              />
              {errors.commission_state && <p className="text-[12px] text-destructive">{errors.commission_state}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Commission Expiry</label>
              <Input
                className="h-8 text-sm"
                type="date"
                value={data.commission_expiry}
                onChange={(e) => setData('commission_expiry', e.target.value)}
              />
              {errors.commission_expiry && <p className="text-[12px] text-destructive">{errors.commission_expiry}</p>}
            </div>
          </FormSection>

          <div className="flex gap-2 pt-2">
            <Button type="submit" size="sm" disabled={processing} className="h-8 text-[13px]">
              Save Changes
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => window.history.back()} className="h-8 text-[13px]">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </NotaryPortalLayout>
  )
}
